// src/App.jsx
import React, { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import Navbar from './components/Navbar.jsx';
import { useLocation } from 'react-router-dom';
import { initOfflineNotifications } from './lib/offlineActivity.js';
import { useAuth } from './Context/AuthContext.jsx';
import { useAuthAdmin } from './Context/useAuthAdmin';
import { supabase } from './lib/supabaseClient';

// layout & guards
import LanguageLabLayout from './components/LanguageLabLayout.jsx';
import AppSideNavLayout from './components/AppSideNavLayout.jsx';
import AssessmentLayout from './components/assessment/AssessmentLayout.jsx';
import ProtectedRoute from './components/ProtectedRoute.jsx';
import SubdomainGate from './components/SubdomainGate.jsx';
import { getSubdomainFromHost } from './lib/hostOrg.js';
import { useUserAccess } from './hooks/queries/useUserData.js';
import { PLAN_ONBOARDING_STATES } from './lib/subscriptionPlans.js';
import { storePendingActivationRedirect } from './lib/activationRedirect.js';
import { getReturnUrlFromSearch } from './lib/authRedirect.js';

// pages: each is its own chunk, loaded when its route first renders.
// Importing them statically puts every page and its 3D/editor libraries in one entry chunk,
// which makes the production build run out of memory and every visitor download all of it.
const Login = lazy(() => import('./Pages/login.jsx'));
const Signup = lazy(() => import('./Pages/signup.jsx'));
// Pages most learners open next; preloadCommonPages() fetches them in the background after the app loads.
// lazy() and the preload share each loader, so a preloaded page is not downloaded twice.
const loadDashboard = () => import('./Pages/dashboard.jsx');
const loadCourseMarketplace = () => import('./Pages/CourseMarketplace.jsx');
const loadCourseCatalog = () => import('./Pages/catalog.jsx');
const loadSubmoduleStudy = () => import('./Pages/study_area.jsx');
const loadAdaptive = () => import('./Pages/Adaptive.jsx');
const loadJourneyDetail = () => import('./Pages/adaptive learning/JourneyDetail.jsx');
const loadPageStudy = () => import('./Pages/adaptive learning/PageStudy.jsx');
const loadQuizAttempt = () => import('./Pages/adaptive learning/QuizAttempt.jsx');
const COMMON_PAGE_LOADERS = [
  loadDashboard,
  loadCourseMarketplace,
  loadCourseCatalog,
  loadSubmoduleStudy,
  // The study area renders lesson content through this lazily loaded component.
  () => import('./components/SubmodulePages.jsx'),
  loadAdaptive,
  loadJourneyDetail,
  loadPageStudy,
  loadQuizAttempt,
];

function preloadCommonPages() {
  const connection = navigator.connection;
  if (connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || '')) return;
  const run = () => COMMON_PAGE_LOADERS.forEach((load) => load().catch(() => {}));
  if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 5000 });
  else window.setTimeout(run, 2000);
}

const Dashboard = lazy(loadDashboard);
const CourseCatalog = lazy(loadCourseCatalog);
const CourseDetail = lazy(() => import('./Pages/coursedetail.jsx'));
const CourseMarketplace = lazy(loadCourseMarketplace);
const CourseLanding = lazy(() => import('./Pages/CourseLanding.jsx'));
const Profile = lazy(() => import('./Pages/profile.jsx'));
const LessonViewer = lazy(() => import('./Pages/lesson.jsx'));
const DynamicStudy = lazy(() => import('./Pages/dynamicstudy.jsx'));
const AdminDashboard = lazy(() => import('./Pages/AdminDashboard'));
const AdminCourseExports = lazy(() => import('./Pages/AdminCourseExports'));
const AdminCourseExportPreview = lazy(() => import('./Pages/AdminCourseExportPreview'));
const AdminCourseExportPreviewH5p = lazy(() => import('./Pages/AdminCourseExportPreviewH5p'));
const AdminGlobalRoleCatalog = lazy(() => import('./Pages/AdminGlobalRoleCatalog.jsx'));
const XRDashboard = lazy(() => import('./Pages/XRDashboard.jsx'));
const AiAvatarAR = lazy(() => import('./Pages/AiAvatarAR.jsx'));
const UsageTracker = lazy(() => import('./Pages/UsageTrackerPage.jsx'));
const SubmoduleStudy = lazy(loadSubmoduleStudy);
const BabylonTest = lazy(() => import('./Pages/BabylonTest.jsx'));
const Adaptive = lazy(loadAdaptive);
const JourneyDetail = lazy(loadJourneyDetail);
const PageStudy = lazy(loadPageStudy);
const QuizAttempt = lazy(loadQuizAttempt);
const CreateCourse = lazy(() => import('./Pages/CreateCourse.jsx'));
const CreateXRCourse = lazy(() => import('./Pages/CreateXRCourse.jsx'));
const CreateVirtualWorld = lazy(() => import('./Pages/CreateVirtualWorld.jsx'));
const EditXRCourse = lazy(() => import('./Pages/EditXRCourse.jsx'));
const EditVirtualWorld = lazy(() => import('./Pages/EditVirtualWorld.jsx'));
const EditCourse = lazy(() => import('./Pages/EditCourse'));
const ContentEditor = lazy(() => import('./Pages/content-editor.jsx'));
const LandingPageEditor = lazy(() => import('./Pages/LandingPageEditor.jsx'));
const ARClassroom = lazy(() => import('./Pages/ARClassroom.jsx'));
const ARAssetDetail = lazy(() => import('./Pages/ARAssetDetail.jsx'));
const ARAssetVR = lazy(() => import('./Pages/ARAssetVR.jsx'));
const ARAssetAdvancedAR = lazy(() => import('./Pages/ARAssetAdvancedAR.jsx'));
const AdminARAssets = lazy(() => import('./Pages/AdminARAssets.jsx'));
const AdminARAssetEditor = lazy(() => import('./Pages/AdminARAssetEditor.jsx'));
const MyActivity = lazy(() => import('./Pages/MyActivity.jsx'));
const LearningDelivery = lazy(() => import('./Pages/LearningDelivery.jsx'));
const AssessmentEngine = lazy(() => import('./Pages/AssessmentEngine.jsx'));
const QuestionPaperDesigner = lazy(() => import('./Pages/QuestionPaperDesigner.jsx'));
const AssessmentPackEditor = lazy(() => import('./Pages/AssessmentPackEditor.jsx'));
const PsychometricPackEditor = lazy(() => import('./Pages/PsychometricPackEditor.jsx'));
const AssessmentRunner = lazy(() => import('./Pages/AssessmentRunner.jsx'));
const Assessments = lazy(() => import('./Pages/Assessments.jsx'));
const AssessmentAttempts = lazy(() => import('./Pages/AssessmentAttempts.jsx'));
const AssessmentReports = lazy(() => import('./Pages/AssessmentReports.jsx'));
const CertificateTemplates = lazy(() => import('./Pages/CertificateTemplates.jsx'));
const AssessmentPassport = lazy(() => import('./Pages/AssessmentPassport.jsx'));
const AssessmentPassportResults = lazy(() => import('./Pages/AssessmentPassportResults.jsx'));
const AssessmentAttemptReport = lazy(() => import('./Pages/AssessmentAttemptReport.jsx'));
const About = lazy(() => import('./Pages/about.jsx'));
const Contact = lazy(() => import('./Pages/contact.jsx'));
const Messages = lazy(() => import('./Pages/Messages.jsx'));
const TermsOfService = lazy(() => import('./Pages/TermsOfService.jsx'));
const PrivacyPolicy = lazy(() => import('./Pages/PrivacyPolicy.jsx'));
const PrivacyRequests = lazy(() => import('./Pages/PrivacyRequests.jsx'));
const AdminPrivacyRequests = lazy(() => import('./Pages/AdminPrivacyRequests.jsx'));
const Notifications = lazy(() => import('./Pages/Notifications.jsx'));
const AdminNotifications = lazy(() => import('./Pages/AdminNotifications.jsx'));
const QuestionBank = lazy(() => import('./Pages/QuestionBank.jsx'));
const QuestionBankHome = lazy(() => import('./Pages/QuestionBankHome.jsx'));
const QuestionBankApprovals = lazy(() => import('./Pages/QuestionBankApprovals.jsx'));
const QuestionBankBanks = lazy(() => import('./Pages/QuestionBankBanks.jsx'));
const QuestionBankBrowse = lazy(() => import('./Pages/QuestionBankBrowse.jsx'));
const QuestionBankBrowseQuestions = lazy(() => import('./Pages/QuestionBankBrowseQuestions.jsx'));
const RefundPolicy = lazy(() => import('./Pages/RefundPolicy.jsx'));
const PaymentHistory = lazy(() => import('./Pages/PaymentHistory.jsx'));
const QuizResults = lazy(() => import('./Pages/QuizResults.jsx'));
const LanguageLab = lazy(() => import('./Pages/LanguageLab.jsx'));
const LanguageLabHome = lazy(() => import('./Pages/LanguageLabHome.jsx'));
const LanguageLabVocabulary = lazy(() => import('./Pages/LanguageLabVocabulary.jsx'));
const LanguageLabMouthMovements = lazy(() => import('./Pages/LanguageLabMouthMovements.jsx'));
const LanguageLabResources = lazy(() => import('./Pages/LanguageLabResources.jsx'));
const LanguageWritingLab = lazy(() => import('./Pages/LanguageWritingLab.jsx'));
const LanguageTutorLab = lazy(() => import('./Pages/LanguageTutorLab.jsx'));
const LanguageTutorLabHome = lazy(() => import('./Pages/LanguageTutorLabHome.jsx'));
const LanguageTutorScenarioSession = lazy(() => import('./Pages/LanguageTutorScenarioSession.jsx'));
const OrgBranding = lazy(() => import('./Pages/OrgBranding.jsx'));
const AdminIntegrationsPage = lazy(() => import('./Pages/AdminIntegrations.jsx'));
const AdminSSOConfig = lazy(() => import('./Pages/AdminSSOConfig.jsx'));
const CourseEditor = lazy(() => import('./Pages/CourseEditor.jsx'));
const CourseHistory = lazy(() => import('./Pages/CourseHistory.jsx'));
const CertificateStudio = lazy(() => import('./Pages/CertificateStudio.jsx'));
const CertificateStudioEditor = lazy(() => import('./Pages/CertificateStudioEditor.jsx'));
const VerifyCourseCertificate = lazy(() => import('./Pages/VerifyCourseCertificate.jsx'));
const AuthCallback = lazy(() => import('./Pages/AuthCallback.jsx'));
const PaymentTracker = lazy(() => import('./Pages/PaymentTracker.jsx'));
const RevenueTracker = lazy(() => import('./Pages/RevenueTracker.jsx'));
const Checkout = lazy(() => import('./Pages/Checkout.jsx'));
const PaymentCallback = lazy(() => import('./Pages/PaymentCallback.jsx'));
const ParentDashboard = lazy(() => import('./Pages/ParentDashboard.jsx'));
const Plans = lazy(() => import('./Pages/Plans.jsx'));
const PlanCheckout = lazy(() => import('./Pages/PlanCheckout.jsx'));
const PlanPaymentCallback = lazy(() => import('./Pages/PlanPaymentCallback.jsx'));
const PlanOrgOnboarding = lazy(() => import('./Pages/PlanOrgOnboarding.jsx'));
const AdminPlanConfig = lazy(() => import('./Pages/AdminPlanConfig.jsx'));
const ActivationCodeGate = lazy(() => import('./Pages/ActivationCodeGate.jsx'));
const SignupProfileCompletion = lazy(() => import('./Pages/SignupProfileCompletion.jsx'));

const CelebrationLayer = lazy(() => import('./components/gamification/CelebrationLayer.jsx'));

function RouteLoading() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center" role="status" aria-label="Loading">
      <div className="w-10 h-10 relative">
        <div className="absolute inset-0 rounded-full border-4 border-blue-100"></div>
        <div className="absolute inset-0 rounded-full border-4 border-blue-500 border-t-transparent animate-spin"></div>
      </div>
    </div>
  );
}

export default function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const hideNavbar = location.pathname === '/login'
    || location.pathname === '/signup'
    || location.pathname === '/activate'
    || location.pathname === '/signup/complete';
  const { user } = useAuth();
  const { userRole, onboardingState, loading: authRoleLoading } = useAuthAdmin();
  const { data: userAccessData } = useUserAccess(user?.id);
  const isParentUser = userRole === 'parent';
  const loggedInHomePath = isParentUser ? '/parent_dashboard' : '/dashboard';
  const loginSearchParams = new URLSearchParams(location.search);
  const hasLoginFlowParam = Boolean(
    loginSearchParams.get('token') ||
    loginSearchParams.get('provider')
  );
  const shouldRedirectLoggedInFromLogin =
    location.pathname === '/login' && Boolean(user) && !hasLoginFlowParam && !authRoleLoading;
  // Guest-route behavior (EduSphere GuestRoute): if already logged in and
  // landing on /login with a deep-link `redirect` param, honor it instead of
  // always bouncing to role home. A logged-in parent still gets caged back
  // to /parent_dashboard by the effect below if the target isn't allowlisted.
  const loginGuestRedirectTarget = getReturnUrlFromSearch(location.search, '') || loggedInHomePath;
  const rawHost = window.location.hostname || '';
  const host = rawHost.split(':')[0];
  const hostSubdomain = getSubdomainFromHost(host);
  const isAssessmentsHost = hostSubdomain === 'assessments' || host === 'assessments.astra-xr.com' || host.startsWith('assessments.');
  const resolvedOnboardingState = userAccessData?.onboarding_state || onboardingState || PLAN_ONBOARDING_STATES.COMPLETED;
  const needsOrgOnboarding = resolvedOnboardingState === PLAN_ONBOARDING_STATES.PENDING_ORG_SETUP;
  const needsSignupProfileCompletion = resolvedOnboardingState === PLAN_ONBOARDING_STATES.PENDING_SIGNUP_PROFILE;
  const needsActivationCode = resolvedOnboardingState === PLAN_ONBOARDING_STATES.PENDING_ACTIVATION_CODE;

  useEffect(() => {
    if (!isAssessmentsHost) return;
    if (location.pathname === '/') {
      navigate('/assessments', { replace: true });
    }
  }, [isAssessmentsHost, location.pathname, navigate]);

  useEffect(() => {
    if (!user || authRoleLoading || !isParentUser) return;
    const allowedParentPaths = new Set(['/parent_dashboard', '/about', '/contact', '/login', '/auth/callback']);
    if (!allowedParentPaths.has(location.pathname)) {
      navigate('/parent_dashboard', { replace: true });
    }
  }, [user, authRoleLoading, isParentUser, location.pathname, navigate]);

  useEffect(() => {
    if (!user || authRoleLoading || !needsOrgOnboarding) return;
    const allowed = new Set(['/plans/onboarding/org', '/auth/callback', '/login', '/about', '/contact']);
    if (!allowed.has(location.pathname)) {
      navigate('/plans/onboarding/org', { replace: true });
    }
  }, [user, authRoleLoading, needsOrgOnboarding, location.pathname, navigate]);

  useEffect(() => {
    if (!user || authRoleLoading || !needsSignupProfileCompletion) return;
    const allowed = new Set(['/signup/complete', '/auth/callback', '/login', '/about', '/contact']);
    if (!allowed.has(location.pathname)) {
      navigate('/signup/complete', { replace: true });
    }
  }, [user, authRoleLoading, needsSignupProfileCompletion, location.pathname, navigate]);

  useEffect(() => {
    if (!user || authRoleLoading || !needsActivationCode) return;
    const allowed = new Set(['/activate', '/auth/callback', '/login', '/about', '/contact']);
    if (!allowed.has(location.pathname)) {
      storePendingActivationRedirect(location.pathname + location.search);
      navigate('/activate', { replace: true });
    }
  }, [user, authRoleLoading, needsActivationCode, location.pathname, location.search, navigate]);
  
  // Initialize offline notifications
  useEffect(() => {
    initOfflineNotifications();
  }, []);

  useEffect(() => {
    if (document.readyState === 'complete') preloadCommonPages();
    else window.addEventListener('load', preloadCommonPages, { once: true });
    return () => window.removeEventListener('load', preloadCommonPages);
  }, []);
  
  // Handle session tokens from URL hash (for subdomain redirects)
  useEffect(() => {
    const handleSessionFromHash = async () => {
      // Skip if on auth callback page (it handles its own tokens)
      if (location.pathname === '/auth/callback') return;
      
      const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
      const access_token = hashParams.get('access_token');
      const refresh_token = hashParams.get('refresh_token');
      
      if (access_token && refresh_token) {
        console.log('[App] Found session tokens in URL hash, setting session');
        try {
          await supabase.auth.setSession({ access_token, refresh_token });
          // Clean up URL hash
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
          console.log('[App] Session set successfully from hash tokens');
        } catch (error) {
          console.error('[App] Failed to set session from hash:', error);
        }
      }
    };
    
    handleSessionFromHash();
  }, [location.pathname]);
  return (
    <>
      <Suspense fallback={null}>
        <CelebrationLayer />
      </Suspense>
      {!hideNavbar && <Navbar />}
      {!hideNavbar && <div style={{ height: 'var(--app-navbar-height, 75px)' }} />}
      <div className={hideNavbar ? undefined : 'app-shell-with-mobile-nav'}>
        <AppSideNavLayout>
        <Suspense fallback={<RouteLoading />}>
        <Routes>
          {/* Public */}
          <Route
            path="/"
            element={
              <SubdomainGate>
                {isAssessmentsHost ? (
                  <Navigate to="/assessments" replace />
                ) : user ? (
                  <Navigate to={loggedInHomePath} replace />
                ) : (
                  <Navigate to="/login" replace />
                )}
              </SubdomainGate>
            }
          />
          <Route
            path="/login"
            element={shouldRedirectLoggedInFromLogin ? <Navigate to={loginGuestRedirectTarget} replace /> : <Login />}
          />
          <Route path="/signup" element={<Signup />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route
            path="/signup/complete"
            element={
              <ProtectedRoute>
                <SignupProfileCompletion />
              </ProtectedRoute>
            }
          />
          <Route
            path="/activate"
            element={
              <ProtectedRoute>
                <ActivationCodeGate />
              </ProtectedRoute>
            }
          />

        {/* Create Course (Admin/Creator) */}
        <Route
          path="/admin/create-course"
          element={
            <ProtectedRoute requireAdminCreatorOnly={true}>
              <CreateCourse />
            </ProtectedRoute>
          }
        />
        <Route
          path="/create-xr-course"
          element={
            <ProtectedRoute requireAdminCreatorOnly={true}>
              <CreateXRCourse />
            </ProtectedRoute>
          }
        />
        <Route
          path="/create-virtual-world"
          element={
            <ProtectedRoute requireAdminCreatorOnly={true}>
              <CreateVirtualWorld />
            </ProtectedRoute>
          }
        />
        <Route
          path="/edit-xr-course/:courseId"
          element={
            <ProtectedRoute requireAdminOrCreator={true}>
              <EditXRCourse />
            </ProtectedRoute>
          }
        />
        <Route
          path="/edit-virtual-world/:courseId"
          element={
            <ProtectedRoute requireAdminOrCreator={true}>
              <EditVirtualWorld />
            </ProtectedRoute>
          }
        />

        {/* Protected */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent_dashboard"
          element={
            <ProtectedRoute requireParentDashboardAccess={true}>
              <ParentDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/privacy-requests"
          element={
            <ProtectedRoute>
              <PrivacyRequests />
            </ProtectedRoute>
          }
        />
        <Route
          path="/privacy-requests/admin"
          element={
            <ProtectedRoute requireAdmin>
              <AdminPrivacyRequests />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <Notifications />
            </ProtectedRoute>
          }
        />
        <Route
          path="/messages"
          element={
            <ProtectedRoute requireAdmin={true}>
              <Messages />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/xr"
          element={
            <ProtectedRoute requireFeature="xr_dashboard">
              <XRDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/xr-selector"
          element={<Navigate to="/dashboard/xr" replace />}
        />
        <Route
          path="/dashboard/xr/ai-avatar"
          element={
            <ProtectedRoute>
              <AiAvatarAR />
            </ProtectedRoute>
          }
        />

        <Route
          path="/ar-classroom"
          element={
            <ProtectedRoute>
              <ARClassroom />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ar-classroom/:id"
          element={
            <ProtectedRoute>
              <ARAssetDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ar-classroom/:id/vr"
          element={
            <ProtectedRoute>
              <ARAssetVR />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ar-classroom/:id/advanced-ar"
          element={
            <ProtectedRoute>
              <ARAssetAdvancedAR />
            </ProtectedRoute>
          }
        />

        {/* AR Assets Admin Portal (Creators/Admins) - must be BEFORE /admin/* super-admin gate */}
        <Route
          path="/admin/ar-assets"
          element={
            <ProtectedRoute requireAdminOrCreator={true}>
              <AdminARAssets />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/ar-assets/new"
          element={
            <ProtectedRoute requireAdminOrCreator={true}>
              <AdminARAssetEditor />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/ar-assets/:id"
          element={
            <ProtectedRoute requireAdminOrCreator={true}>
              <AdminARAssetEditor />
            </ProtectedRoute>
          }
        />


        {/* Courses IA: marketplace -> all courses -> optional landing -> detail */}
        <Route path="/courses"           element={<SubdomainGate><CourseMarketplace /></SubdomainGate>} />
        <Route path="/courses/all"       element={<SubdomainGate><CourseCatalog /></SubdomainGate>} />
        <Route path="/courses/:courseId/landing" element={<SubdomainGate><CourseLanding /></SubdomainGate>} />
        <Route path="/courses/:courseId" element={<CourseDetail />} />
        <Route path="/catalog" element={<Navigate to="/courses/all" replace />} />

        {/* Lesson Viewer */}
        <Route path="/lessons/:lessonId" element={<SubdomainGate><LessonViewer /></SubdomainGate>} />

        {/* Dynamic Study */}
        <Route
          path="/visualize"
          element={
            <ProtectedRoute requireFeature="visualize_book">
              <DynamicStudy />
            </ProtectedRoute>
          }
        />

        <Route 
          path="/admin/usage-tracker" 
          element={
            <ProtectedRoute requireSuperAdmin={true}>
              <UsageTracker />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/global-role-catalog"
          element={
            <ProtectedRoute requireSuperAdmin={true}>
              <AdminGlobalRoleCatalog />
            </ProtectedRoute>
          }
        />

        <Route 
          path="/revenue-tracker" 
          element={
            <ProtectedRoute requireSuperAdmin={true}>
              <RevenueTracker />
            </ProtectedRoute>
          }
        />

        {/* Admin Dashboard (Super Admin only) */}
        <Route 
          path="/admin/*" 
          element={
            <ProtectedRoute requireSuperAdmin={true}>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/course-exports"
          element={
            <ProtectedRoute requireAdminCreatorOnly={true}>
              <AdminCourseExports />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/course-exports/:courseId/preview"
          element={
            <ProtectedRoute requireAdminCreatorOnly={true}>
              <AdminCourseExportPreview />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/course-exports/:courseId/preview-h5p"
          element={
            <ProtectedRoute requireAdminCreatorOnly={true}>
              <AdminCourseExportPreviewH5p />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/notifications"
          element={
            <ProtectedRoute requireAdminCreatorOnly={true}>
              <AdminNotifications />
            </ProtectedRoute>
          }
        />

        {/* Integrations Admin (Super Admin only) */}
        <Route 
          path="/admin/integrations" 
          element={
            <ProtectedRoute requireSuperAdmin={true}>
              <AdminIntegrationsPage />
            </ProtectedRoute>
          }
        />

        {/* SSO Config (Admin + Super Admin) */}
        <Route
          path="/admin/sso-config"
          element={
            <ProtectedRoute requireAdmin={true}>
              <AdminSSOConfig />
            </ProtectedRoute>
          }
        />

        {/* Payment Tracker (Super Admin only) */}
        <Route 
          path="/payment-tracker" 
          element={
            <ProtectedRoute requireSuperAdmin={true}>
              <PaymentTracker />
            </ProtectedRoute>
          }
        />

        {/* Checkout (Protected) */}
        <Route 
          path="/checkout/:courseId" 
          element={
            <ProtectedRoute>
              <Checkout />
            </ProtectedRoute>
          }
        />

        {/* Payment History */}
        <Route
          path="/payment-history"
          element={
            <ProtectedRoute>
              <PaymentHistory />
            </ProtectedRoute>
          }
        />

        {/* Payment Callback (Public - handles post-payment redirect) */}
        <Route 
          path="/payment/callback" 
          element={<PaymentCallback />} 
        />

        <Route path="/plans" element={<Plans />} />
        <Route
          path="/plans/checkout"
          element={
            <ProtectedRoute>
              <PlanCheckout />
            </ProtectedRoute>
          }
        />
        <Route path="/plans/callback" element={<PlanPaymentCallback />} />
        <Route
          path="/plans/onboarding/org"
          element={
            <ProtectedRoute>
              <PlanOrgOnboarding />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/plan-config"
          element={
            <ProtectedRoute requireSuperAdmin={true}>
              <AdminPlanConfig />
            </ProtectedRoute>
          }
        />

        {/* Course generation history (org-scoped) */}
        <Route
          path="/course-history"
          element={
            <ProtectedRoute requireAdminOrCreator={true}>
              <CourseHistory />
            </ProtectedRoute>
          }
        />

        {/* Course Editor (Admin or Creator) */}
        <Route
          path="/course-editor"
          element={
            <ProtectedRoute requireAdminOrCreator={true}>
              <CourseEditor />
            </ProtectedRoute>
          }
        />
        <Route
          path="/certificate-studio"
          element={
            <ProtectedRoute requireAdminOrCreator={true}>
              <CertificateStudio />
            </ProtectedRoute>
          }
        />
        <Route
          path="/certificate-studio/:templateId"
          element={
            <ProtectedRoute requireAdminOrCreator={true}>
              <CertificateStudioEditor />
            </ProtectedRoute>
          }
        />

        {/* Organization Branding */}
        <Route
          path="/admin/org-branding"
          element={
            <ProtectedRoute requireSameOrg={true}>
              <OrgBranding />
            </ProtectedRoute>
          }
        />



        {/* Submodule Study */}
        <Route path="/study_area/:submoduleId" element={<SubdomainGate><SubmoduleStudy /></SubdomainGate>} />

        {/* Test Babylon Standalone */}
        <Route path="/babylon-test" element={<SubdomainGate><BabylonTest /></SubdomainGate>} />

        {/* Adaptive Learning Catalog */}
        <Route
          path="/adaptive"
          element={
            <ProtectedRoute requireFeature="adaptive_learning">
              <Adaptive />
            </ProtectedRoute>
          }
        />

        {/* Journey Detail */}
        <Route
          path="/adaptive/journey/:journeyId"
          element={
            <ProtectedRoute requireFeature="adaptive_learning">
              <JourneyDetail />
            </ProtectedRoute>
          }
        />

        {/* Page Study */}
        <Route
          path="/adaptive/page/:pageId"
          element={
            <ProtectedRoute requireFeature="adaptive_learning">
              <PageStudy />
            </ProtectedRoute>
          }
        />

        {/* Quiz Attempt */}
        <Route
          path="/adaptive/quiz/:quizId"
          element={
            <ProtectedRoute requireFeature="adaptive_learning">
              <QuizAttempt />
            </ProtectedRoute>
          }
        />

        {/* Edit Course */}
        <Route
          path="/admin/edit-course/:courseId"
          element={
            <ProtectedRoute requireAdminCreatorOnly={true}>
              <EditCourse />
            </ProtectedRoute>
          }
        />

        {/* Content Editor (Course-wide and Page) */}
        <Route
          path="/content-editor/:courseId"
          element={
            <ProtectedRoute requireAdminCreatorOnly={true}>
              <ContentEditor />
            </ProtectedRoute>
          }
        />
        <Route
          path="/content-editor/page/:pageId"
          element={
            <ProtectedRoute requireAdminCreatorOnly={true}>
              <ContentEditor />
            </ProtectedRoute>
          }
        />
        <Route
          path="/content-editor/landing/:courseId"
          element={
            <ProtectedRoute requireAdminCreatorOnly={true}>
              <LandingPageEditor />
            </ProtectedRoute>
          }
        />

        {/* My Activity */}
        <Route
          path="/my-activity"
          element={
            <ProtectedRoute>
              <MyActivity />
            </ProtectedRoute>
          }
        />

        {/* Learning & Delivery */}
        <Route
          path="/learning-delivery"
          element={
            <ProtectedRoute requireAdminOrCreator={true}>
              <LearningDelivery />
            </ProtectedRoute>
          }
        />

        {/* Assessment Engine - fullscreen flows (no sidebar) */}
        <Route
          path="/assessment-engine/pack/:packId/run"
          element={
            <ProtectedRoute requireFeature="assessment_engine">
              <AssessmentRunner />
            </ProtectedRoute>
          }
        />
        <Route
          path="/assessment-engine/pack/:packId/edit"
          element={
            <ProtectedRoute
              requireAdminOrCreator={true}
              requireFeature="assessment_engine"
              unauthorizedTitle="Oops! Un-authorised access."
              unauthorizedMessage="Are you looking for assessments?"
              unauthorizedActions={[
                { label: 'Dashboard', to: '/dashboard', variant: 'secondary' },
                { label: 'Assessments', to: '/assessments', variant: 'primary' },
              ]}
            >
              <AssessmentPackEditor />
            </ProtectedRoute>
          }
        />
        <Route
          path="/assessment-engine/pack/:packId/psychometric/edit"
          element={
            <ProtectedRoute
              requireAdminOrCreator={true}
              requireFeature="assessment_engine"
              unauthorizedTitle="Oops! Un-authorised access."
              unauthorizedMessage="Are you looking for assessments?"
              unauthorizedActions={[
                { label: 'Dashboard', to: '/dashboard', variant: 'secondary' },
                { label: 'Assessments', to: '/assessments', variant: 'primary' },
              ]}
            >
              <PsychometricPackEditor />
            </ProtectedRoute>
          }
        />

        {/* Assessment hub layout */}
        <Route
          element={
            <ProtectedRoute requireFeature="assessment_engine">
              <AssessmentLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/assessments" element={<Assessments />} />
          <Route path="/assessments/passport/results" element={<AssessmentPassportResults />} />
          <Route path="/assessments/passport" element={<AssessmentPassport />} />
          <Route path="/assessments/attempt/:attemptId/report" element={<AssessmentAttemptReport />} />
          <Route
            path="/assessment-engine"
            element={
              <ProtectedRoute
                requireAdminOrCreator={true}
                unauthorizedTitle="Oops! Un-authorised access."
                unauthorizedMessage="Are you looking for assessments?"
                unauthorizedActions={[
                  { label: 'Dashboard', to: '/dashboard', variant: 'secondary' },
                  { label: 'Assessments', to: '/assessments', variant: 'primary' },
                ]}
              >
                <AssessmentEngine />
              </ProtectedRoute>
            }
          />
          <Route
            path="/assessment-engine/reports"
            element={
              <ProtectedRoute
                requireAdminOrCreator={true}
                unauthorizedTitle="Oops! Un-authorised access."
                unauthorizedMessage="Are you looking for assessments?"
                unauthorizedActions={[
                  { label: 'Dashboard', to: '/dashboard', variant: 'secondary' },
                  { label: 'Assessments', to: '/assessments', variant: 'primary' },
                ]}
              >
                <AssessmentReports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/assessment-engine/certificates"
            element={
              <ProtectedRoute
                requireAdminOrCreator={true}
                unauthorizedTitle="Oops! Un-authorised access."
                unauthorizedMessage="Are you looking for assessments?"
                unauthorizedActions={[
                  { label: 'Dashboard', to: '/dashboard', variant: 'secondary' },
                  { label: 'Assessments', to: '/assessments', variant: 'primary' },
                ]}
              >
                <CertificateTemplates />
              </ProtectedRoute>
            }
          />
          <Route
            path="/assessment-engine/paper-designer"
            element={
              <ProtectedRoute
                requireAdminOrCreator={true}
                unauthorizedTitle="Oops! Un-authorised access."
                unauthorizedMessage="Are you looking for assessments?"
                unauthorizedActions={[
                  { label: 'Dashboard', to: '/dashboard', variant: 'secondary' },
                  { label: 'Assessments', to: '/assessments', variant: 'primary' },
                ]}
              >
                <QuestionPaperDesigner />
              </ProtectedRoute>
            }
          />
          <Route
            path="/assessment-engine/question-bank"
            element={
              <ProtectedRoute
                requireAdminOrCreator={true}
                unauthorizedTitle="Oops! Un-authorised access."
                unauthorizedMessage="Are you looking for assessments?"
                unauthorizedActions={[
                  { label: 'Dashboard', to: '/dashboard', variant: 'secondary' },
                  { label: 'Assessments', to: '/assessments', variant: 'primary' },
                ]}
              >
                <QuestionBankHome />
              </ProtectedRoute>
            }
          />
          <Route
            path="/assessment-engine/question-bank/browse"
            element={
              <ProtectedRoute
                requireAdminOrCreator={true}
                unauthorizedTitle="Oops! Un-authorised access."
                unauthorizedMessage="Are you looking for assessments?"
                unauthorizedActions={[
                  { label: 'Dashboard', to: '/dashboard', variant: 'secondary' },
                  { label: 'Assessments', to: '/assessments', variant: 'primary' },
                ]}
              >
                <QuestionBankBrowse />
              </ProtectedRoute>
            }
          />
          <Route
            path="/assessment-engine/question-bank/browse/:bankId"
            element={
              <ProtectedRoute
                requireAdminOrCreator={true}
                unauthorizedTitle="Oops! Un-authorised access."
                unauthorizedMessage="Are you looking for assessments?"
                unauthorizedActions={[
                  { label: 'Dashboard', to: '/dashboard', variant: 'secondary' },
                  { label: 'Assessments', to: '/assessments', variant: 'primary' },
                ]}
              >
                <QuestionBankBrowseQuestions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/assessment-engine/question-bank/create"
            element={
              <ProtectedRoute
                requireAdminOrCreator={true}
                unauthorizedTitle="Oops! Un-authorised access."
                unauthorizedMessage="Are you looking for assessments?"
                unauthorizedActions={[
                  { label: 'Dashboard', to: '/dashboard', variant: 'secondary' },
                  { label: 'Assessments', to: '/assessments', variant: 'primary' },
                ]}
              >
                <QuestionBank mode="create" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/assessment-engine/question-bank/approvals"
            element={
              <ProtectedRoute
                requireAdminOrCreator={true}
                unauthorizedTitle="Oops! Un-authorised access."
                unauthorizedMessage="Are you looking for assessments?"
                unauthorizedActions={[
                  { label: 'Dashboard', to: '/dashboard', variant: 'secondary' },
                  { label: 'Assessments', to: '/assessments', variant: 'primary' },
                ]}
              >
                <QuestionBankApprovals />
              </ProtectedRoute>
            }
          />
          <Route
            path="/assessment-engine/question-bank/banks"
            element={
              <ProtectedRoute
                requireAdminOrCreator={true}
                unauthorizedTitle="Oops! Un-authorised access."
                unauthorizedMessage="Are you looking for assessments?"
                unauthorizedActions={[
                  { label: 'Dashboard', to: '/dashboard', variant: 'secondary' },
                  { label: 'Assessments', to: '/assessments', variant: 'primary' },
                ]}
              >
                <QuestionBankBanks />
              </ProtectedRoute>
            }
          />
          <Route
            path="/assessment-engine/pack/:packId/attempts"
            element={
              <ProtectedRoute
                requireAdminOrCreator={true}
                unauthorizedTitle="Oops! Un-authorised access."
                unauthorizedMessage="Are you looking for assessments?"
                unauthorizedActions={[
                  { label: 'Dashboard', to: '/dashboard', variant: 'secondary' },
                  { label: 'Assessments', to: '/assessments', variant: 'primary' },
                ]}
              >
                <AssessmentAttempts />
              </ProtectedRoute>
            }
          />
        </Route>
        {/* Quiz Results */}
        <Route
          path="/quiz-results"
          element={
            <ProtectedRoute requireAdminOrCreator={true}>
              <QuizResults />
            </ProtectedRoute>
          }
        />

        {/* Language Lab */}
        <Route
          path="/language-lab"
          element={
            <ProtectedRoute requireFeature="language_tools">
              <LanguageLabLayout />
            </ProtectedRoute>
          }
        >
          <Route
            index
            element={
              <ProtectedRoute requireFeature={["language_tools", "language_lab"]}>
                <LanguageLabHome />
              </ProtectedRoute>
            }
          />
          <Route
            path="practice"
            element={
              <ProtectedRoute requireFeature={["language_tools", "language_lab"]}>
                <LanguageLab />
              </ProtectedRoute>
            }
          />
          <Route
            path="vocabulary"
            element={
              <ProtectedRoute requireFeature={["language_tools", "language_lab"]}>
                <LanguageLabVocabulary />
              </ProtectedRoute>
            }
          />
          <Route
            path="mouth-movements"
            element={
              <ProtectedRoute requireFeature={["language_tools", "language_lab"]}>
                <LanguageLabMouthMovements />
              </ProtectedRoute>
            }
          />
          <Route
            path="writing"
            element={
              <ProtectedRoute requireFeature={["language_tools", "writing_lab"]}>
                <LanguageWritingLab />
              </ProtectedRoute>
            }
          />
          <Route
            path="tutor"
            element={
              <ProtectedRoute requireFeature={["language_tools", "ai_tutor_lab"]}>
                <LanguageTutorLabHome />
              </ProtectedRoute>
            }
          />
          <Route
            path="tutor/talk"
            element={
              <ProtectedRoute requireFeature={["language_tools", "ai_tutor_lab"]}>
                <LanguageTutorLab />
              </ProtectedRoute>
            }
          />
          <Route
            path="tutor/scenarios/:scenarioId"
            element={
              <ProtectedRoute requireFeature={["language_tools", "ai_tutor_lab"]}>
                <LanguageTutorScenarioSession />
              </ProtectedRoute>
            }
          />
          <Route
            path="resources"
            element={
              <ProtectedRoute requireFeature={["language_tools", "language_lab"]}>
                <LanguageLabResources />
              </ProtectedRoute>
            }
          />
        </Route>

        {/* About & Contact */}
        <Route path="/about" element={<SubdomainGate><About /></SubdomainGate>} />
        <Route path="/contact" element={<SubdomainGate><Contact /></SubdomainGate>} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />
        <Route path="/verify-certificate/:code" element={<VerifyCourseCertificate />} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
        </AppSideNavLayout>
      </div>
    </>
  );
}
